import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { EFormService } from '../../e-forms/e-form.service';
import { User } from '../../user/user.model';
import { PortalMenuHelper } from '../portal-menu-helper';
import { PortalMenuComponent } from '../portal-menu.component';
import { Classroom360Service } from './../../../classroom360/classroom360.service';
import { LoginService } from './../../../shared-modules/login/login.service';
import { Student360Service } from './../../../student360/student360.service';
import { Portal } from './../../portal.model';
import { StudentService } from './../../student/student.service';
import { PortalMenuPreferences } from './../../user-preferences/portal-menu-preferences.model';
import { UserPreferencesService } from './../../user-preferences/user-preferences.service';
import { UserService } from './../../user/user.service';

// TODO: update preferences
@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'application-menu',
    templateUrl: './application-menu.component.html',
    styleUrls: ['./application-menu.component.css']
})
export class ApplicationMenuComponent implements OnInit, OnDestroy {

    // Injected Properties
    @Output() navigated = new EventEmitter<void>();
    @ViewChild(PortalMenuComponent) portalMenuComponent: PortalMenuComponent;

    // Properties
    portals: Portal[] = [];
    preferences: PortalMenuPreferences;
    isStudent = false;

    // Subscription Properties
    subscriptions: Subscription[] = [];

    // Class Functions
    constructor(
        private userService: UserService,
        private classroom360Service: Classroom360Service,
        private student360Service: Student360Service,
        private studentService: StudentService,
        private userPreferencesService: UserPreferencesService,
        private loginService: LoginService,
        private eFormService: EFormService
    ) { }

    // Event Functions
    ngOnInit(): void {

        // Setting up the user because the user stream isn't a behavior subject (although, it probably should be)
        this.setupUser(this.userService.currentUser);

        // Performing subscriptions to state changes
        this.subscriptions.push(

            // User Subscription
            this.userService
                .observableCurrentUserStream$
                .subscribe(user => {
                    this.setupUser(user);
                    this.updatePortals();
                }),

            // Staff Subscriptions
            this.userPreferencesService
                .observableStaffPortalMenuPreferencesStream$
                .subscribe(preferences => {
                    if (!this.isStudent) {
                        this.updatePortals(this.portals, preferences);
                    }
                }),
            this.classroom360Service
                .observableStaffAssignedPortalsStream$
                .subscribe(portals => {
                    if (!this.isStudent) {
                        this.updatePortals(portals, this.preferences);
                    }
                }),

            // Student Subscriptions
            this.userPreferencesService
                .observableStudentPortalMenuPreferencesStream$
                .subscribe(preferences => {
                    if (
                        this.isStudent
                        && this.studentService.selectedStudent
                    ) {
                        this.updatePortals(this.portals, preferences);
                    }
                }),
            this.student360Service
                .observableParentAssignedPortalsStream$
                .subscribe(portals => {
                    if (
                        this.isStudent
                        && this.studentService.selectedStudent
                    ) {
                        this.updatePortals(portals, this.preferences);
                        this.checkEformPermission();
                    }
                })
        );
    }

    ngOnDestroy(): void { }

    // Callable Methods

    // UI Functions
    portalExpanded(portal: Portal) {
        // PRE: a portal menu is expanded
        // POST: saves the portal preferences and emits the click event
        if (portal.IsGroup && this.preferences) {
            let portalMenuItem = this.preferences.PortalMenus.filter(preferencePortal =>
                (
                    !preferencePortal.ParentGroupName || preferencePortal.ParentGroupName.toLowerCase() == portal.GroupName.toLowerCase()
                )
                && preferencePortal.GroupName.toLowerCase() == portal.PortalName.toLowerCase()
                && portal.IsGroup
            ).sort((a, b) => {
                if (a.ParentGroupName != null && a.ParentGroupName == portal.GroupName) {
                    if (a.ParentGroupName == b.ParentGroupName && a.Id > b.Id) {
                        return -3;
                    }
                    else {
                        return -2;
                    }
                }
                else if (a.ParentGroupName != null) {
                    return -1;
                }
                else {
                    return 1;
                }
            })[0];

            if (portalMenuItem) {
                portalMenuItem.ParentGroupName = portal.GroupName;
                portalMenuItem.Expanded = portal.Expanded;
            } else {
                portalMenuItem = {
                    ParentGroupName: portal.GroupName,
                    GroupName: portal.PortalName,
                    GroupIcon: portal.GroupIcon,
                    Expanded: portal.Expanded,
                    Id: portal.Id
                };
                this.preferences.PortalMenus.push(portalMenuItem);
            }
            this.saveMenuPreferences();
        }
    }

    expandCollapseClick(expandAll: number) {
        this.preferences.PortalMenus.forEach(portal => {
            portal.Expanded = expandAll;
        });

        this.saveMenuPreferences();
    }

    saveMenuPreferences() {
        const studentId = this.userService.userType == 2 && this.studentService.selectedStudent != null && this.studentService.selectedStudent.Id != null ? this.studentService.selectedStudent.Id : 0;
        const xmlStream: string[] = [];
        xmlStream.push(JSON.stringify(this.preferences));
        this.userPreferencesService.saveUserPreferences(
            studentId,
            '/OneSISPortalMenuPreferences',
            xmlStream,
            0,
            ''
        ).subscribe(() => {
            if (this.isStudent) {
                this.userPreferencesService.studentPortalMenuPreferences = this.preferences;
            } else {
                this.userPreferencesService.staffPortalMenuPreferences = this.preferences;
            }
        });
    }

    portalNavigated(portal: Portal) {
        // PRE: a portal is used to navigate
        // POST: sends the navigation event
        this.navigated.emit();
    }

    // Helper Functions

    // Internal Functions
    private updatePortals(
        portals?: Portal[],
        preferences?: PortalMenuPreferences
    ) {
        if (portals) {
            this.portals = portals;
        }
        if (preferences) {
            this.preferences = preferences;
        }
        this.portals = PortalMenuHelper.organizePortals(
            portals,
            this.preferences,
            this.loginService.LoginSettings.V10 && this.userService.staffUserStudentLocked
        );
    }

    private setupUser(user: User) {
        if (user) {
            this.isStudent = user.UserType != undefined
                && user.UserType != 1;

            // Getting portal preferences if we haven't already
            // NOTE: This triggers the above preference subscriptions
            if (!this.preferences) {
                if (this.isStudent) {
                    if (this.studentService.selectedStudent) {
                        this.userPreferencesService.getStudentMenuPreferences(this.studentService.selectedStudent.Id).subscribe();
                    }
                } else {
                    this.userPreferencesService.getStaffMenuPreferences().subscribe();
                }
            }
        }
    }

    checkEformPermission() {
        if (!this.loginService.LoginSettings.V10) {
            this.eFormService.eFormHeadLinePermission = false;
            if (this.student360Service.allParentPortals && this.student360Service.allParentPortals.filter(o => o.PortalName == 'e-Forms').length > 0) {
                this.eFormService.eFormHeadLinePermission = true;
            }
            this.eFormService.observableeFormHeadLinePermission.next();
        }
    }

    sideNavOpened() {
        this.portalMenuComponent.sideNavOpened();
    }
}